/**
 * 조직 상세 + 매장 목록 페이지
 * HomeContent와 완전히 동일한 디자인
 */
import { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Store, Tag } from 'lucide-react';
import { SearchBar } from '@/components/SearchBar';
import { RestaurantCard } from '@/components/RestaurantCard';
import { QrSourceBanner } from '@/components/QrSourceBanner';
import { useOrganizationStores } from '@/hooks/useOrganizationStores';
import { useLocationAnchor } from '@/hooks/useLocationAnchor';
import type { Store as LocalStore } from '@/data';

export function OrganizationDetailPage() {
  const navigate = useNavigate();
  // useParams로 URL 파라미터 추출 - Routes/Route 구조에서 정상 작동
  const { orgParam } = useParams<{ orgParam: string }>();
  const { stores, organization, loading, error } = useOrganizationStores(orgParam);
  const anchor = useLocationAnchor();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredStores = useMemo(() => {
    if (!searchQuery.trim()) return stores;
    const query = searchQuery.toLowerCase();
    return stores.filter(
      (store: LocalStore) =>
        store.name?.toLowerCase().includes(query) ||
        store.address?.toLowerCase().includes(query)
    );
  }, [stores, searchQuery]);

  const handleStoreClick = (storeId: string) => {
    navigate(`/restaurant/${storeId}`);
  };

  const handleBrandsClick = () => {
    navigate(`/org/${orgParam}/brands`);
  };

  return (
    <div className="flex-1 pb-[calc(5rem+2vw)] p-[0px] m-[0px]">
      <SearchBar
        placeholder="Buscar sucursales..."
        value={searchQuery}
        onChange={setSearchQuery}
      />

      {/* #365: QR source 컨텍스트 배너 — qr-table 진입 시 픽업 폴백 안내 */}
      <QrSourceBanner anchor={anchor} />

      {/* Brands link - 브랜드 바로가기 배너 */}
      <div className="px-[min(5vw,1.25rem)] pt-[min(3vw,0.75rem)]">
        <button
          onClick={handleBrandsClick}
          className="w-full p-3 bg-gradient-to-r from-secondary/10 to-secondary/10
            rounded-lg border border-secondary/20
            flex items-center justify-between
            hover:from-secondary/20 hover:to-secondary/20 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-secondary" />
            <span className="text-secondary font-medium">Ver marcas</span>
          </div>
          <span className="text-secondary/60 text-sm">Filtrar por marca</span>
        </button>
      </div>

      <div className="p-[min(5vw,1.25rem)]">
        <div className="flex items-center justify-between m-[0px]">
          <h2 className="text-[16px] font-medium text-foreground">
            {organization?.name || 'Cargando...'}
          </h2>
        </div>
        <p className="text-[14px] text-muted-foreground mb-[min(3vw,1rem)]">
          {filteredStores.length} sucursales
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-2 text-muted-foreground">Cargando...</span>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">{error.message}</p>
          </div>
        ) : filteredStores.length === 0 ? (
          <div className="text-center py-8">
            <Store className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {searchQuery
                ? 'No se encontraron sucursales'
                : 'No hay sucursales disponibles'}
            </p>
          </div>
        ) : (
          <div className="space-y-[min(2vw,0.5rem)]">
            {filteredStores.map((store: LocalStore, index: number) => (
              <div
                key={store.id}
                className="transform transition-all duration-500 ease-out opacity-0 translate-y-4 animate-fadeInUp"
                style={{
                  animationDelay: `${index * 100}ms`,
                  animationFillMode: 'forwards',
                }}
              >
                <div className="hover:scale-[1.02] transition-transform duration-200 hover:shadow-lg rounded-2xl">
                  <RestaurantCard
                    id={String(store.id)}
                    name={store.name}
                    cuisine={store.address || ''}
                    rating={0}
                    deliveryTime=""
                    distance=""
                    imageUrl={store.imageUrl || ''}
                    isOpen={store.status === '영업중'}
                    onClick={handleStoreClick}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
